const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

const TOKEN_KEY = 'hmtdao2.token';
const REFRESH_KEY = 'hmtdao2.refreshToken';

let currentToken: string | null = localStorage.getItem(TOKEN_KEY);
let currentRefreshToken: string | null = localStorage.getItem(REFRESH_KEY);

// Coalesce concurrent refresh attempts: if N requests hit 401 at the same time
// we only make ONE /auth/refresh call and the rest await its result.
let inflightRefresh: Promise<string | null> | null = null;

type RefreshListener = () => void;
const refreshFailureListeners = new Set<RefreshListener>();

export function onRefreshFailure(listener: RefreshListener): () => void {
  refreshFailureListeners.add(listener);
  return () => { refreshFailureListeners.delete(listener); };
}

export function setToken(token: string | null) {
  currentToken = token;
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getToken(): string | null {
  return currentToken;
}

export function setRefreshToken(token: string | null) {
  currentRefreshToken = token;
  if (token) localStorage.setItem(REFRESH_KEY, token);
  else localStorage.removeItem(REFRESH_KEY);
}

export function getRefreshToken(): string | null {
  return currentRefreshToken;
}

export function setSession(token: string | null, refreshToken: string | null) {
  setToken(token);
  setRefreshToken(refreshToken);
}

async function refreshAccessToken(): Promise<string | null> {
  const rt = currentRefreshToken;
  if (!rt) return null;

  if (inflightRefresh) return inflightRefresh;

  inflightRefresh = (async () => {
    try {
      const res = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: rt }),
      });
      if (!res.ok) {
        setSession(null, null);
        refreshFailureListeners.forEach((fn) => fn());
        return null;
      }
      const body = (await res.json()) as { token: string; refreshToken: string };
      setSession(body.token, body.refreshToken);
      return body.token;
    } catch {
      setSession(null, null);
      refreshFailureListeners.forEach((fn) => fn());
      return null;
    } finally {
      inflightRefresh = null;
    }
  })();

  return inflightRefresh;
}

interface ApiInit extends RequestInit {
  _retried?: boolean;
  // When true, the request is HMAC-signed per PROJECT_SECURITY_RULES §6.1 —
  // required for money-moving endpoints. Signatures are computed at send-time
  // so that a 401-triggered refresh automatically re-signs with the new token.
  signed?: boolean;
}

import {
  deriveSigningKey,
  computeSignature,
  generateNonce,
  generateIdempotencyKey,
} from './signing';

async function attachSignatureHeaders(
  headers: Record<string, string>,
  method: string,
  path: string,
  rawBody: string,
  idempotencyKey: string,
): Promise<void> {
  if (!currentToken) {
    throw new Error('Cannot sign request: no access token');
  }
  const timestamp = Date.now().toString();
  const nonce = generateNonce();
  const key = await deriveSigningKey(currentToken);
  const signature = await computeSignature(
    key,
    method,
    `${BASE_URL}${path}`,
    timestamp,
    nonce,
    rawBody,
  );
  headers['X-Timestamp'] = timestamp;
  headers['X-Nonce'] = nonce;
  headers['X-Idempotency-Key'] = idempotencyKey;
  headers['X-Signature'] = signature;
}

export async function api<T = unknown>(path: string, init: ApiInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const rawBody = typeof init.body === 'string' ? init.body : '';

  // An idempotency key must survive a 401-refresh retry so the server treats
  // both attempts as the same logical request.
  let idempotencyKey: string | undefined;
  if (init.signed) {
    idempotencyKey = (init as ApiInit & { _idempotencyKey?: string })._idempotencyKey
      ?? generateIdempotencyKey();
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (currentToken) headers.Authorization = `Bearer ${currentToken}`;
  if (init.signed && idempotencyKey) {
    await attachSignatureHeaders(headers, method, path, rawBody, idempotencyKey);
  }

  const res = await fetch(`${BASE_URL}${path}`, { ...init, headers });

  const isAuthEndpoint =
    path === '/auth/refresh' ||
    path === '/auth/logout' ||
    path === '/auth/login' ||
    path === '/auth/register';
  if (res.status === 401 && !init._retried && !isAuthEndpoint && currentRefreshToken) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      return api<T>(path, {
        ...init,
        _retried: true,
        ...(idempotencyKey ? { _idempotencyKey: idempotencyKey } : {}),
      } as ApiInit);
    }
  }

  const text = await res.text();
  const body = text ? JSON.parse(text) : undefined;

  if (!res.ok) {
    const msg = body?.error ?? `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return body as T;
}
